import { Link } from 'react-router-dom';
import { DocSection, Callout, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'collections-folders', label: 'Collections & Folders' },
  { id: 'toolbar', label: 'The toolbar' },
  { id: 'media-table', label: 'The media table' },
  { id: 'row-menu', label: 'The row menu' },
  { id: 'analysis-progress', label: 'Analysis progress' },
  { id: 'panel', label: 'In the Premiere panel' },
];

export default function Body() {
  return (
    <>
      <DocSection id="collections-folders" title="Collections & Folders">
        <p>
          The <strong>Media</strong> tab is where you bring footage in and put it to work: import
          files and folders, organize them, and drive analysis. The whole tab is a drop target — drag
          files or folders in from Windows and ClipRadar imports them.
        </p>
        <p>
          A sidebar toggles between two ways of browsing what you've imported, above an{' '}
          <span style={kbd}>All Media</span> shortcut with a running count:
        </p>
        <ul>
          <li>
            <strong>Collections</strong> — hand-picked groups you build yourself.{' '}
            <span style={kbd}>+ New collection</span> creates one; each collection's ⋮ menu renames or
            deletes it, and you can drag to reorder the list. A separate{' '}
            <strong>Project Collections</strong> group is created automatically by the{' '}
            <Link to="/docs/platforms/premiere-panel">Premiere panel</Link>, one per open Premiere
            project — you can activate one to filter the table, but not rename or reorder it.
          </li>
          <li>
            <strong>Folders</strong> — disk folders you've pointed ClipRadar at. Each folder's ⋮ menu
            offers <span style={kbd}>Rescan folder</span>,{' '}
            <span style={kbd}>Watch while open</span> / <span style={kbd}>Stop watching</span>, and{' '}
            <span style={kbd}>Remove folder</span>. A watched folder shows an eye icon and picks up new
            files dropped into it — but only <em>while ClipRadar is running</em>. It's not a background
            service that watches your disk when the app is closed. A folder ClipRadar can no longer
            find renders in red.
          </li>
        </ul>
        <p>
          In the Premiere panel, this sidebar is replaced by a compact bar that switches between your
          library and the current project's media, alongside the same filters.
        </p>
      </DocSection>

      <DocSection id="toolbar" title="The toolbar">
        <p>
          Above the table, the toolbar shows the active scope (collection, folder, or{' '}
          <span style={kbd}>All Media</span>) with a count and a ✕ to jump back to everything, plus{' '}
          <span style={kbd}>Add files</span>, <span style={kbd}>Add folders</span>, and{' '}
          <span style={kbd}>Refresh</span>.
        </p>
        <DocShot
          slug="media-tab"
          shot="filters-popover"
          alt="The Media tab Filters popover with Type, Status, Faces, Speech, In-search and Show hidden options"
          caption="The Filters popover, opened from the toolbar."
        />
        <p>
          A <strong>Filters</strong> popover narrows the table by Type, analysis Status, whether Faces
          or Speech have run, whether a source is included in search, and a{' '}
          <span style={kbd}>Show hidden</span> toggle for rows you've hidden from the list. A debounced
          name filter next to it searches filenames as you type.
        </p>
      </DocSection>

      <DocSection id="media-table" title="The media table">
        <p>
          Every imported source is a row in a sortable table. Click any column header to sort by it:
        </p>
        <DocShot
          slug="media-tab"
          shot="table"
          alt="Annotated media table with numbered callouts"
          callouts={[
            { n: 1, label: 'Type, filename, and duration' },
            { n: 2, label: 'SEARCH — the include-in-search dot for this source' },
            { n: 3, label: 'VISUAL / SPEECH / FACES — the three analysis cells' },
            { n: 4, label: 'ADDED — when the source was imported' },
          ]}
        />
        <p>
          Columns: a select checkbox, <strong>type</strong>, <strong>FILENAME</strong>,{' '}
          <strong>SEARCH</strong>, <strong>VISUAL</strong>, <strong>SPEECH</strong>,{' '}
          <strong>FACES</strong>, <strong>DURATION</strong>, and <strong>ADDED</strong>.
        </p>
        <ul>
          <li>
            <strong>Analysis cells</strong> (VISUAL / SPEECH / FACES) read at a glance: a quiet green
            check means <span style={kbd}>done</span>, a dashed box with a{' '}
            <span style={kbd}>+</span> means eligible — click to queue that pass — a blue check means
            queued, and a dimmed cell means the pass doesn't apply. Shift+click selects a range of
            cells at once. When rows still have work pending, a{' '}
            <span style={kbd}>Queue all</span> sub-row appears — click a column's box there to queue
            that pass for every eligible row. See{' '}
            <Link to="/docs/concepts/analyzing-media">Analyzing your footage</Link> for what each pass
            actually does.
          </li>
          <li>
            <strong>SEARCH column</strong> — a blue magnifier means the source is included in search
            results; a hollow ring means it's excluded. Click to toggle without deleting anything.
          </li>
          <li>
            <strong>Speech-problem badges</strong> replace the SPEECH cell when a file's audio is
            damaged or suspect: amber means usable but worth checking, red means unusable, and neutral
            means no audio track at all.
          </li>
        </ul>
        <DocShot
          slug="media-tab"
          shot="speech-problem-popover"
          alt="A speech-problem popover with Re-transcribe now, Create repaired copy, View transcript and Looks fine actions"
          caption="Clicking a speech-problem badge opens the fix popover."
        />
        <p>
          Click a badge to open a popover explaining the exact problem, with{' '}
          <span style={kbd}>Re-transcribe now</span>, <span style={kbd}>Create repaired copy</span>,{' '}
          <span style={kbd}>View transcript</span>, and <span style={kbd}>Looks fine — dismiss</span>{' '}
          actions. The full playbook lives in{' '}
          <Link to="/docs/guides/fix-speech-problems">Fix speech & transcript problems</Link>.
        </p>
        <p>
          Select rows with their checkbox (Shift+click for a range) or by clicking a filename. Rows
          from the bundled demo library carry a <span style={kbd}>Demo</span> badge.
        </p>
      </DocSection>

      <DocSection id="row-menu" title="The row menu">
        <p>
          Right-click a row — or use the hover ⋮ button on desktop — to open its menu. Right-clicking
          inside a multi-row selection acts on every selected row at once, with counts in the labels
          (e.g. "Analyze All (N)").
        </p>
        <DocShot
          slug="media-tab"
          shot="row-menu"
          alt="The media row right-click context menu"
        />
        <p>
          The menu covers renaming (display name only, with a{' '}
          <span style={kbd}>Reset to original name</span> option),{' '}
          <span style={kbd}>Reveal in File Explorer</span>,{' '}
          <span style={kbd}>Reveal Visual Analysis</span> / <span style={kbd}>Reveal Speech
          Analysis</span>, <span style={kbd}>Relink missing file…</span> for a source that moved,
          every analysis action (<span style={kbd}>Analyze All</span>,{' '}
          <span style={kbd}>Analyze/Re-analyze Visual</span>,{' '}
          <span style={kbd}>Re-segment Visual</span>,{' '}
          <span style={kbd}>Re-index for New Model</span> when a source needs it,{' '}
          <span style={kbd}>View Transcript</span>,{' '}
          <span style={kbd}>Analyze/Re-analyze Speech</span>,{' '}
          <span style={kbd}>Find Faces/Re-analyze Faces</span>,{' '}
          <span style={kbd}>Create Repaired Copy</span> for damaged audio),{' '}
          <span style={kbd}>Hide from list</span> / <span style={kbd}>Unhide</span>,{' '}
          <span style={kbd}>Add to Collection…</span> / <span style={kbd}>Remove from
          Collection</span>, and removing the source: a type-to-confirm{' '}
          <span style={kbd}>Delete from Library…</span> for analyzed rows, or a plain{' '}
          <span style={kbd}>Remove from Library</span> for rows that haven't been analyzed yet.
        </p>
      </DocSection>

      <DocSection id="analysis-progress" title="Analysis progress">
        <p>
          Queued work runs at the bottom of the tab: an analyze bar shows the current job, a two-bar
          indexing-progress readout tracks overall completion, and a status bar summarizes what's left.
          Analysis runs on your <Link to="/docs/concepts/models-and-your-gpu">GPU</Link> — scene
          detection, visual embeddings, speech transcription, and face recognition are covered pass by
          pass in <Link to="/docs/concepts/analyzing-media">Analyzing your footage</Link>.
        </p>
        <p>
          You can queue more analysis while a job is already running — it joins the same run rather
          than waiting. Banners surface above the table when sources need re-embedding for a newer
          model, when a transcript failed to embed, or when speech problems need attention.
        </p>
      </DocSection>

      <DocSection id="panel" title="In the Premiere panel">
        <p>
          The Media tab works the same way inside the Premiere panel, with the sidebar swapped for a
          compact bar (library vs. current-project scope, filters, and the name filter) to fit the
          narrower width. Row menus, analysis cells, and speech-problem badges behave identically.
        </p>
        <Callout kind="tip">
          A watched folder in the panel behaves the same as on desktop: it only picks up new files{' '}
          <em>while ClipRadar is open</em>, in either shell — not a background daemon.
        </Callout>
      </DocSection>
    </>
  );
}
